/**
 * Setup roadmap step definitions and shared route constants.
 * Admin infra steps stay under /admin; data-manager workflows under /manage.
 */

import { isPdsaMode } from '../utils/appBranding';

/** Site-admin / infra setup steps (Setup Roadmap under /admin). */
export const ADMIN_SETUP_STEPS = [
    {
        id: 'initial',
        number: 1,
        title: 'Initial Server Configuration',
        description: 'Configure server mode (hub/spoke), node name, and contact information',
        route: '/admin/setup/initial',
    },
    {
        id: 'network',
        number: 2,
        title: 'Network Connectivity',
        description: 'Connect to other BDSA nodes in the network (girder-network plugin)',
        route: '/admin/setup/network',
    },
    {
        id: 'agents',
        number: 3,
        title: 'Agent Management',
        description: 'Configure and manage RAG agents (girder-agent plugin)',
        route: '/admin/setup/agents',
    },
    {
        id: 'cilogon',
        number: 4,
        title: 'CILogon OAuth (optional)',
        description: 'Configure CILogon OAuth for institutional login (girder-oauth plugin)',
        route: '/admin/setup/cilogon',
    },
];

/** Data Manager definition + Case Builder steps (under /manage). */
export const DATA_MANAGER_STEPS = [
    {
        id: 'stain',
        number: 1,
        title: 'Stain Definitions',
        description: 'Define stains for BDSA metadata',
        route: '/manage/stain',
    },
    {
        id: 'region',
        number: 2,
        title: 'Region Definitions',
        description: 'Define regions for BDSA metadata',
        route: '/manage/region',
    },
    {
        id: 'block',
        number: 3,
        title: 'Protocols & Blocks',
        description: 'Create sectioning protocols and define blocks (and their regions)',
        route: '/manage/block',
    },
    {
        id: 'casebuilder',
        number: 4,
        title: 'Case Builder',
        description: 'Create cases, assign slides, and release to the network',
        route: '/manage/casebuilder',
    },
    {
        id: 'import',
        number: 5,
        pdsaOnly: true,
        title: 'PathPresenter import',
        description: 'Import public PathPresenter shares into Girder folders',
        route: '/manage/import',
    },
];

/** @deprecated Use ADMIN_SETUP_STEPS — kept as alias for older imports. */
export const SETUP_ROADMAP_STEPS = ADMIN_SETUP_STEPS;

export const AGENT_SETUP_BASE =
    ADMIN_SETUP_STEPS.find((s) => s.id === 'agents')?.route ?? '/admin/setup/agents';

export const STAIN_SETUP_ROUTE =
    DATA_MANAGER_STEPS.find((s) => s.id === 'stain')?.route ?? '/manage/stain';

export const REGION_SETUP_ROUTE =
    DATA_MANAGER_STEPS.find((s) => s.id === 'region')?.route ?? '/manage/region';

export const BLOCK_SETUP_ROUTE =
    DATA_MANAGER_STEPS.find((s) => s.id === 'block')?.route ?? '/manage/block';

export const CASEBUILDER_BASE =
    DATA_MANAGER_STEPS.find((s) => s.id === 'casebuilder')?.route ?? '/manage/casebuilder';

export const IMPORT_ROUTE =
    DATA_MANAGER_STEPS.find((s) => s.id === 'import')?.route ?? '/manage/import';

export const DATA_MANAGER_HOME = '/manage/data';

export const DATA_MANAGER_WORKSPACE_LABEL = 'Data Manager workspace';

export const AGENT_SETUP_BACK_LABEL = 'Back to Agent Management';

function stepFromCatalog(steps, id) {
    const step = steps.find((s) => s.id === id);
    if (!step) {
        return null;
    }
    return {
        id: step.id,
        number: step.number,
        total: steps.length,
        title: step.title,
        route: step.route,
        description: step.description,
    };
}

function visibleDataManagerStepsRaw() {
    return DATA_MANAGER_STEPS.filter(
        (step) => !step.pdsaOnly || isPdsaMode(),
    );
}

function numberedSteps(steps) {
    return steps.map((step, index) => ({
        ...step,
        number: index + 1,
        total: steps.length,
    }));
}

/** Steps shown in the Data Manager roadmap and left nav (PDSA-gated). */
export function getVisibleDataManagerSteps() {
    return numberedSteps(visibleDataManagerStepsRaw());
}

/** @returns {{ id, number, total, title, route, description } | null} */
export function getVisibleDataManagerStep(id) {
    return stepFromCatalog(getVisibleDataManagerSteps(), id);
}

/** @returns {{ id, number, total, title, route, description } | null} */
export function getAdminStep(id) {
    return stepFromCatalog(ADMIN_SETUP_STEPS, id);
}

/** @returns {{ id, number, total, title, route, description } | null} */
export function getDataManagerStep(id) {
    return stepFromCatalog(DATA_MANAGER_STEPS, id);
}
